import React, { useRef, useEffect, useState, useCallback, forwardRef } from 'react';
import p5 from 'p5';
import { Range } from 'react-range';
import { palettes as builtInPalettes } from './Palettes';
import ReactDOM from 'react-dom';

const HelpModal = ({ onClose }) => {
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>&times;</button>
        <div class="help-content">
        <h2>Getting Started with Geometrism</h2>
          <p>
            Geometrism is your creative tool for making colorful geometric art. Here’s a quick guide to what you can do:
          </p>
          <ol>
            <li>
              <strong>Add Shapes:</strong> 
              <p>
                Click on the canvas to add a shape. The shape type comes from your current <em>Click-to-Add Shape Options</em>.
              </p>
            </li>
            <li>
              <strong>Click-to-Add Shape Options:</strong> 
              <p>
                Configure properties for shapes you add by clicking – adjust size, opacity, border, rotation, and if using partial circles, set the arc start and arc end. Choose the color option via radio buttons to use either a specific color from your palette or a random one.
              </p>
            </li>
            <li>
              <strong>Edit Selected Shape:</strong> 
              <p>
                After adding a shape, click it to select. The <em>Edit Selected Shape</em> panel (located right below the manual options) lets you modify its properties, adjust its layering order, or delete it.
              </p>
            </li>
            <li>
              <strong>Random Shape Generator Options:</strong> 
              <p>
                Automatically create shapes with random properties. Adjust random size, opacity, rotation, border settings, and shape color. Use the dropdown (which reveals checkboxes) to choose which shape types are allowed for random generation.
              </p>
            </li>
            <li>
              <strong>Freehand Drawing Options:</strong> 
              <p>
                Enable freehand mode to draw custom curves. Adjust line width and color as desired.
              </p>
            </li>
            <li>
              <strong>Grid Options:</strong> 
              <p>
                Turn on the grid overlay and enable snapping to the grid for precise shape placement.
              </p>
            </li>
            <li>
              <strong>Canvas Controls:</strong> 
              <p>
                Manage your overall artwork – change the background color, undo/redo changes, clear the canvas, export your art as PNG, SVG, or JSON, and load a saved JSON file using the "Load JSON" button.
              </p>
            </li>
            <li>
              <strong>Palette Manager:</strong> 
              <p>
                Select from predefined palettes or create your own custom palette. Click the <em>Edit</em> button to modify, rename, or delete a palette.
              </p>
            </li>
            <li>
              <strong>Dark Mode:</strong> 
              <p>
                Toggle between light and dark themes using the button at the bottom of the sidebar. Dark Mode adjusts backgrounds, controls, and text for comfortable viewing.
              </p>
            </li>
            <li>
              <strong>Keyboard Shortcuts:</strong>
              <ul>        
                <li>Ctrl+Z / Cmd+Z: Undo</li>
                <li>Ctrl+Y / Cmd+Y: Redo</li>
                <li>Ctrl+S / Cmd+S: Export JSON</li>
                <li>Ctrl+E / Cmd+E: Export PNG</li>
                <li>Delete / Backspace: Delete the selected shape</li>
                <li>Ctrl+C / Cmd+C: Copy the selected shape</li>
                <li>Ctrl+V / Cmd+V: Paste the copied shape</li>
                <li>R / r: Generate a random shape</li>
                </ul>
            </li>
          </ol>
          <p>
            For any further assistance, simply click the Help button again. Enjoy exploring your creativity with Geometrism!
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

const PaletteModal = ({
  onClose,
  newPaletteName,
  setNewPaletteName,
  newPaletteColors,
  setNewPaletteColors,
  saveCustomPalette,
  onDelete
}) => {
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{newPaletteName ? "Edit Palette" : "Create Custom Palette"}</h2>
        <div className="control-row-edit">
          <label>Palette name:</label>
          <input
            type="text"
            value={newPaletteName}
            onChange={(e) => setNewPaletteName(e.target.value)}
            placeholder="Enter palette name"
          />
        </div>
        <div className="control-column">
          <label>Colors:</label>
          {newPaletteColors.map((col, index) => (
            <div key={index} className="control-row-edit">
              <input
                type="color"
                value={col}
                onChange={(e) => {
                  const updated = [...newPaletteColors];
                  updated[index] = e.target.value;
                  setNewPaletteColors(updated);
                }}
                className="color-input"
              />
              <button onClick={() => setNewPaletteColors(prev => prev.filter((_, i) => i !== index))}>
                Remove
              </button>
            </div>
          ))}
          <button onClick={() => setNewPaletteColors(prev => [...prev, "#ffffff"])}>
            Add color
          </button>
        </div>
        <div className="buttons-row">
          <button onClick={saveCustomPalette}>Save palette</button>
          <button onClick={onDelete}>Delete palette</button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Canvas = forwardRef(({ selectedPalette, onPaletteChange }, ref) => {
  const canvasRef = useRef(null);
  const p5Instance = useRef(null);
  const [allPalettes, setAllPalettes] = useState([...builtInPalettes]);
  const paletteRef = useRef(selectedPalette);
  const xSliderCommitFlag = useRef(false);
  const ySliderCommitFlag = useRef(false);
  const borderWidthSliderCommitFlag = useRef(false);
  const sizeSliderCommitFlag = useRef(false);
  const copiedShapeRef = useRef(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const draggingShapeId = useRef(null);
  const opacitySliderCommitFlag = useRef(false);
  const borderOpacitySliderCommitFlag = useRef(false);
  const rotationSliderCommitFlag = useRef(false);
  const [colorPalettesOpen, setColorPalettesOpen] = useState(true);
  const [randomSettingsOpen, setRandomSettingsOpen] = useState(true);
  const [manualAddSettingsOpen, setManualAddSettingsOpen] = useState(true);
  const [globalControlsOpen, setGlobalControlsOpen] = useState(true);
  const [editShapeSettingsOpen, setEditShapeSettingsOpen] = useState(true);
  const [freehandSettingsOpen, setFreehandSettingsOpen] = useState(true);
  const [gridSettingsOpen, setGridSettingsOpen] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [allowedShapesOpen, setAllowedShapesOpen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(80);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const gridSettingsRef = useRef({ showGrid, gridSize, snapToGrid });
  useEffect(() => {
    gridSettingsRef.current = { showGrid, gridSize, snapToGrid };
  }, [showGrid, gridSize, snapToGrid]);

  const [defaultShapeSettings, setDefaultShapeSettings] = useState({
    size: 80,
    color: selectedPalette.colors[0] || "#000000",
    opacity: 255,
    borderWidth: 0,
    borderColor: "#000000",
    borderOpacity: 255,
    rotation: 0,
    arcStart: 90,
    arcEnd: 270,
    useRandomColor: true,
    width: 80,
    height: 40,
  });

  const defaultSettingsRef = useRef(defaultShapeSettings);
  useEffect(() => {
    defaultSettingsRef.current = defaultShapeSettings;
  }, [defaultShapeSettings]);

  const freehandRef = useRef(null);
  const [freehandMode, setFreehandMode] = useState(false);
  const freehandModeRef = useRef(freehandMode);
  useEffect(() => {
    freehandModeRef.current = freehandMode;
  }, [freehandMode]);

  const [randomShapeSettings, setRandomShapeSettings] = useState({
    size: { min: 20, max: 120 },
    opacity: { min: 150, max: 255 },
    borderWidth: { min: 0, max: 0 },
    borderOpacity: { min: 0, max: 255 },
    rotation: { min: 0, max: 360 },
    colorOption: "random",
    specificColor: "#000000"
  });

  const [randomBorderColorOption, setRandomBorderColorOption] = useState("random"); 
  const [randomBorderColor, setRandomBorderColor] = useState("#000000"); 

  const [shapes, setShapes] = useState([]);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [selectedShapeType, setSelectedShapeType] = useState('circle');
  const defaultBgColor = "#ffffff";
  const [bgColor, setBgColor] = useState(defaultBgColor);
  const [bgColorOverridden, setBgColorOverridden] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [newPaletteName, setNewPaletteName] = useState('');
  const [newPaletteColors, setNewPaletteColors] = useState(['#ffffff', '#ffffff', '#ffffff']);
  const [freehandSettings, setFreehandSettings] = useState({
    lineWidth: 10,
    color: "#000000"
  });
  const freehandSettingsRef = useRef(freehandSettings);
  useEffect(() => {
    freehandSettingsRef.current = freehandSettings;
  }, [freehandSettings]);

  const shapesRef = useRef(shapes);
  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);

  const selectedShapeTypeRef = useRef(selectedShapeType);
  useEffect(() => {
    selectedShapeTypeRef.current = selectedShapeType;
  }, [selectedShapeType]);

  useEffect(() => {
    if (selectedShapeType === "line") {
      setDefaultShapeSettings(prev => ({
        ...prev,
        width: 5,
        height: 80,
      }));
    }
    else if (selectedShapeType === "rectangle") {
      setDefaultShapeSettings(prev => ({
        ...prev,
        width: 80,
        height: 40,
      }));
    }
  }, [selectedShapeType]);

  const bgColorRef = useRef(bgColor);
  useEffect(() => {
    bgColorRef.current = bgColor;
  }, [bgColor]);

  useEffect(() => {
    if (!bgColorOverridden) {
      if (darkMode) {
        setBgColor("#000000");
      } else {
        setBgColor(defaultBgColor);
      }
    }
  }, [darkMode, bgColorOverridden]);

  const [allowedShapes, setAllowedShapes] = useState({
    circle: true,
    square: true,
    triangle: true,
    pentagon: true,
    hexagon: true,
    rectangle: true,
    line: true,
    partialCircle: true,
  });

  useEffect(() => {
    paletteRef.current = selectedPalette;
  }, [selectedPalette]);

  const addShapeAndSaveState = useCallback((newShape) => {
    saveUndoSnapshot();
    setShapes(prev => [...prev, newShape]);
  }, []);

  const handleShapeEdit = (property, value) => {
    if (selectedShapeId !== null) {
      saveUndoSnapshot();
      const updatedShapes = shapes.map(shape =>
        shape.id === selectedShapeId ? { ...shape, [property]: value } : shape
      );
      setShapes(updatedShapes);
    }
  };

  const handleDeleteShape = () => {
    if (selectedShapeId !== null) {
      const updatedShapes = shapes.filter(shape => shape.id !== selectedShapeId);
      saveUndoSnapshot();
      setShapes(updatedShapes);
      setSelectedShapeId(null);
    }
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previous = undoStack[undoStack.length - 1];
      setUndoStack(prev => prev.slice(0, prev.length - 1));
      setRedoStack(prev => [JSON.parse(JSON.stringify(shapes)), ...prev]);
      setShapes(previous);
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const next = redoStack[0];
      setRedoStack(prev => prev.slice(1));
      setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(shapes))]);
      setShapes(next);
    }
  };

  const moveShape = (direction) => {
    if (selectedShapeId !== null) {
      const index = shapes.findIndex(s => s.id === selectedShapeId);
      let updated = [...shapes];
      if (direction === 'forward' && index < shapes.length - 1) {
        [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      } else if (direction === 'backward' && index > 0) {
        [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
      }
      saveUndoSnapshot();
      setShapes(updated);
      setSelectedShapeId(updated.find(s => s.id === selectedShapeId)?.id);
    }
  };

  const isPointInsideShape = (p, shape, mx, my) => {
    switch (shape.type) {
      case 'circle':
        return p.dist(mx, my, shape.x, shape.y) <= shape.size / 2;
      case 'square':
        return Math.abs(mx - shape.x) <= shape.size / 2 && Math.abs(my - shape.y) <= shape.size / 2;
      case 'rectangle':
        return Math.abs(mx - shape.x) <= shape.width / 2 && Math.abs(my - shape.y) <= shape.height / 2;
      case 'freehand':
        return shape.points.some(pt => p.dist(mx, my, pt.x, pt.y) < 5);
      default:
        return p.dist(mx, my, shape.x, shape.y) <= shape.size / 2;
    }
  };

  const handleLoad = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const loadedData = JSON.parse(event.target.result);
        if (loadedData.shapes !== undefined && loadedData.bgColor !== undefined) {
          setShapes(loadedData.shapes);
          setBgColor(loadedData.bgColor);
        } else {
          setShapes(loadedData);
        }
        setUndoStack([]);
        setRedoStack([]);
      } catch (err) {
        console.error("Error parsing file:", err);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };
  
  const handleSaveToFile = () => {
    const saveData = {
      shapes: shapes,
      bgColor: bgColor
    };
    const dataStr = JSON.stringify(saveData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'geometrism.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCanvasClick = (p) => {
    let posX = p.mouseX;
    let posY = p.mouseY;
    if (gridSettingsRef.current.snapToGrid) {
      posX = Math.round(posX / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
      posY = Math.round(posY / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
    }
    const defaults = defaultSettingsRef.current;
    const newColor = defaults.useRandomColor
      ? p.color(paletteRef.current.colors[Math.floor(Math.random() * paletteRef.current.colors.length)]).toString()
      : defaults.color;
    const newShape = {
      id: Date.now(),
      type: selectedShapeTypeRef.current,
      x: posX,
      y: posY,
      size: defaults.size,
      color: newColor,
      opacity: defaults.opacity,
      borderWidth: defaults.borderWidth,
      borderColor: defaults.borderColor,
      borderOpacity: defaults.borderOpacity,
      rotation: defaults.rotation,
      width: selectedShapeTypeRef.current === 'rectangle' ? defaults.size : (selectedShapeTypeRef.current === 'line' ? defaults.size : undefined),
      height: selectedShapeTypeRef.current === 'rectangle' ? defaults.size / 2 : (selectedShapeTypeRef.current === 'line' ? defaults.size / 2 : undefined),
      arcStart: selectedShapeTypeRef.current === 'partialCircle' ? defaults.arcStart : undefined,
      arcEnd: selectedShapeTypeRef.current === 'partialCircle' ? defaults.arcEnd : undefined,
      width: (selectedShapeTypeRef.current === "rectangle" || selectedShapeTypeRef.current === "line")
                ? defaultSettingsRef.current.width
                : undefined,
      height: (selectedShapeTypeRef.current === "rectangle" || selectedShapeTypeRef.current === "line")
                ? defaultSettingsRef.current.height
                : undefined,
    };
    addShapeAndSaveState(newShape);
    setSelectedShapeId(newShape.id);
  };

  const generateRandomShape = () => {
    const allTypes = ['circle', 'square', 'triangle', 'pentagon', 'hexagon', 'rectangle', 'line', 'partialCircle'];
    const allowedTypes = allTypes.filter(type => allowedShapes[type]);
    if (allowedTypes.length === 0) {
      alert('Please select at least one allowed shape type.');
      return;
    }
    const randomType = allowedTypes[Math.floor(Math.random() * allowedTypes.length)];
    const randomSize = Math.floor(Math.random() * (randomShapeSettings.size.max - randomShapeSettings.size.min + 1)) + randomShapeSettings.size.min;
    const randomOpacity = Math.floor(Math.random() * (randomShapeSettings.opacity.max - randomShapeSettings.opacity.min + 1)) + randomShapeSettings.opacity.min;
    const randomBorderWidth = Math.floor(Math.random() * (randomShapeSettings.borderWidth.max - randomShapeSettings.borderWidth.min + 1)) + randomShapeSettings.borderWidth.min;
    const randomBorderOpacity = Math.floor(Math.random() * (randomShapeSettings.borderOpacity.max - randomShapeSettings.borderOpacity.min + 1)) + randomShapeSettings.borderOpacity.min;
    const randomRotation = Math.floor(Math.random() * (randomShapeSettings.rotation.max - randomShapeSettings.rotation.min + 1)) + randomShapeSettings.rotation.min;
    const randomX = Math.floor(Math.random() * (window.innerWidth - 300));
    const randomY = Math.floor(Math.random() * window.innerHeight);
    const paletteColors = paletteRef.current.colors;
    const randomColor = paletteColors[Math.floor(Math.random() * paletteColors.length)];
    const shapeColor = randomShapeSettings.colorOption === "specific"
      ? randomShapeSettings.specificColor
      : randomColor;

    let finalBorderColor;
    if (randomBorderColorOption === "specific") {
      finalBorderColor = randomBorderColor;
    } else if (randomBorderColorOption === "random") {
      finalBorderColor = paletteColors[Math.floor(Math.random() * paletteColors.length)];
    } else if (randomBorderColorOption === "same") {
      finalBorderColor = randomColor;
    }

    const newShape = {
      id: Date.now(),
      type: randomType,
      x: randomX,
      y: randomY,
      size: randomSize,
      color: shapeColor,
      opacity: randomOpacity,
      borderWidth: randomBorderWidth,
      borderColor: finalBorderColor,
      borderOpacity: randomBorderOpacity,
      rotation: randomRotation,
      width: randomType === 'rectangle' ? randomSize : (randomType === 'line' ? randomSize : undefined),
      height: randomType === 'rectangle' ? randomSize / 2 : (randomType === 'line' ? randomSize / 2 : undefined),
      arcStart: randomType === 'partialCircle' ? 270 : undefined,
      arcEnd: randomType === 'partialCircle' ? 90 : undefined
    };

    addShapeAndSaveState(newShape);
  };

  const drawShape = (p, shape) => {
    const { x, y, size, type, color, opacity, rotation, width, height, borderWidth, borderColor, borderOpacity, arcStart, arcEnd, points } = shape;

    if (type === 'freehand') {
      p.push();
      p.noFill();
      p.stroke(shape.color);
      p.strokeWeight(shape.lineWidth || 2);
      p.beginShape();
      shape.points.forEach(pt => p.vertex(pt.x, pt.y));
      p.endShape();
      p.pop();
      return;
    }

    const fillCol = p.color(color);
    fillCol.setAlpha(opacity);
    p.fill(fillCol);

    if (shape.id !== selectedShapeId && borderWidth > 0) {
      const strokeCol = p.color(borderColor);
      strokeCol.setAlpha(borderOpacity);
      p.stroke(strokeCol);
      p.strokeWeight(borderWidth);
    }

    p.push();
    p.translate(x, y);
    p.rotate(p.radians(rotation));
    switch (type) {
      case 'circle':
        p.circle(0, 0, size);
        break;
      case 'square':
        p.rect(-size/2, -size/2, size, size);
        break;
      case 'rectangle':
        p.rect(-width/2, -height/2, width, height);
        break;
      case 'line':
        p.rect(-width/2, -height/2, width, height);
        break;
      case 'triangle':
        p.triangle(0, 0, size, 0, 0, size);
        break;
      case 'pentagon':
        p.beginShape();
        for (let i = 0; i < 5; i++) {
          const angle = p.TWO_PI / 5 * i;
          const px = size * Math.cos(angle);
          const py = size * Math.sin(angle);
          p.vertex(px, py);
        }
        p.endShape(p.CLOSE);
        break;
      case 'hexagon':
        p.beginShape();
        for (let i = 0; i < 6; i++) {
          const angle = p.TWO_PI / 6 * i;
          const px = size * Math.cos(angle);
          const py = size * Math.sin(angle);
          p.vertex(px, py);
        }
        p.endShape(p.CLOSE);
        break;
      case 'partialCircle':
        p.arc(0, 0, size, size, p.radians(arcStart), p.radians(arcEnd), p.PIE);
        break; 
      case 'freehand':
        p.push();
        p.noFill();
        p.stroke(shape.color);
        p.strokeWeight(shape.lineWidth || 2);
        p.beginShape();
        shape.points.forEach(pt => p.vertex(pt.x, pt.y));
        p.endShape();
        break;
      default:
        p.circle(0, 0, size);
        break;
    }
    p.pop();
  };

  useEffect(() => {

    const cleanup = () => {
      if (p5Instance.current) {
        p5Instance.current.remove();
        p5Instance.current = null;
      }
    };

    const sketch = (p) => {
      const canvasWidth = window.innerWidth - 300;
      const canvasHeight = window.innerHeight;

      function drawScene(pg) {
        // Set the background
        pg.background(bgColorRef.current);
      
        // Draw grid if enabled.
        if (gridSettingsRef.current.showGrid) {
          pg.stroke(200, 200, 200, 100);
          pg.strokeWeight(1);
          for (let x = 0; x < pg.width; x += gridSettingsRef.current.gridSize) {
            pg.line(x, 0, x, pg.height);
          }
          for (let y = 0; y < pg.height; y += gridSettingsRef.current.gridSize) {
            pg.line(0, y, pg.width, y);
          }
        }
      
        // Draw each shape.
        shapesRef.current.forEach(shape => {
          if (shape.borderWidth > 0) {
            const strokeCol = pg.color(shape.borderColor);
            strokeCol.setAlpha(shape.borderOpacity);
            pg.stroke(strokeCol);
            pg.strokeWeight(shape.borderWidth);
          } else {
            pg.noStroke();
          }
          const fillCol = pg.color(shape.color);
          fillCol.setAlpha(shape.opacity);
          pg.fill(fillCol);
          drawShape(pg, shape);
        });
      
        // Draw freehand if active.
        if (freehandModeRef.current && freehandRef.current) {
          pg.push();
          pg.noFill();
          pg.stroke(freehandRef.current.color);
          pg.strokeWeight(freehandRef.current.lineWidth || 2);
          pg.beginShape();
          const pts = freehandRef.current.points;
          if (pts.length > 1) {
            pg.curveVertex(pts[0].x, pts[0].y);
          }
          pts.forEach(pt => pg.curveVertex(pt.x, pt.y));
          if (pts.length > 1) {
            pg.curveVertex(pts[pts.length - 1].x, pts[pts.length - 1].y);
          }
          pg.endShape();
          pg.pop();
        }
      }
      
      p.setup = () => {
        p.pixelDensity(2);
        p.createCanvas(canvasWidth, canvasHeight);
        p.frameRate(30);
      };

      p.draw = () => {
        drawScene(p);
      };

      p.mousePressed = () => {

        if (p.mouseX < 0 || p.mouseX > p.width || p.mouseY < 0 || p.mouseY > p.height) return;

        if (freehandModeRef.current) {
          let startX = p.mouseX;
          let startY = p.mouseY;
          if (gridSettingsRef.current.snapToGrid) {
            startX = Math.round(startX / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
            startY = Math.round(startY / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
          }

          const newColor = freehandSettingsRef.current.color;
          freehandRef.current = {
            id: Date.now(),
            type: 'freehand',
            points: [{ x: startX, y: startY }],
            color: newColor,
            lineWidth: freehandSettingsRef.current.lineWidth
          };
          return;
        }

        let shapeToDrag = null;
        if (selectedShapeId !== null) {
          shapeToDrag = shapesRef.current.find(shape =>
            shape.id === selectedShapeId && isPointInsideShape(p, shape, p.mouseX, p.mouseY)
          );
        }
        if (!shapeToDrag) {
          for (let i = shapesRef.current.length - 1; i >= 0; i--) {
            if (isPointInsideShape(p, shapesRef.current[i], p.mouseX, p.mouseY)) {
              shapeToDrag = shapesRef.current[i];
              setSelectedShapeId(shapeToDrag.id);
              break;
            }
          }
        }
        if (shapeToDrag) {
          isDragging.current = true;
          draggingShapeId.current = shapeToDrag.id;
          dragOffset.current = {
            x: p.mouseX - shapeToDrag.x,
            y: p.mouseY - shapeToDrag.y
          };
          return;
        }

        handleCanvasClick(p);
      };

      p.mouseDragged = () => {
        if (!freehandModeRef.current && isDragging.current && draggingShapeId.current) {
          let newX = p.mouseX - dragOffset.current.x;
          let newY = p.mouseY - dragOffset.current.y;
          if (gridSettingsRef.current.snapToGrid) {
            newX = Math.round(newX / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
            newY = Math.round(newY / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
          }
          const updatedShapes = shapesRef.current.map(shape =>
            shape.id === draggingShapeId.current ? { ...shape, x: newX, y: newY } : shape
          );
          setShapes(updatedShapes);
        } else if (freehandModeRef.current && freehandRef.current) {
          let x = p.mouseX;
          let y = p.mouseY;
          if (gridSettingsRef.current.snapToGrid) {
            x = Math.round(x / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
            y = Math.round(y / gridSettingsRef.current.gridSize) * gridSettingsRef.current.gridSize;
          }
          const pts = freehandRef.current.points;
          if (pts.length === 0 || p.dist(x, y, pts[pts.length - 1].x, pts[pts.length - 1].y) > 3) {
            freehandRef.current.points.push({ x, y });
          }
        }
      };

      p.mouseReleased = () => {
        if (freehandModeRef.current && freehandRef.current) {
          const finalizedShape = {
            ...freehandRef.current,
            points: [...freehandRef.current.points]
          };
          addShapeAndSaveState(finalizedShape);
          freehandRef.current = null;
        } else if (!freehandModeRef.current && isDragging.current) {
          isDragging.current = false;
          draggingShapeId.current = null;
        }
      };

      p.exportImage = (format = 'png') => {
        const scaleFactor = 3;
        const exportWidth = canvasWidth * scaleFactor;
        const exportHeight = canvasHeight * scaleFactor;
        const pg = p.createGraphics(exportWidth, exportHeight);
        pg.pixelDensity(2);

        pg.push();
        pg.scale(scaleFactor);
        drawScene(pg);
        pg.pop();
      
        if (format === 'png') {
          p.saveCanvas(pg, 'geometrism', 'png');
        } else if (format === 'svg') {
          p.saveCanvas(pg, 'geometrism', 'svg');
        }
      };
                  
      p.saveArt = (format) => {
        p.exportImage(format);
      };

      p5Instance.current = p;
    };

    new p5(sketch, canvasRef.current);
    return cleanup;
  }, []);

  React.useImperativeHandle(ref, () => ({
    saveArt: (format) => {
      if (p5Instance.current) {
        p5Instance.current.saveArt(format);
      }
    }
  }));

  const saveUndoSnapshot = () => {
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(shapesRef.current))]);
    setRedoStack([]);
  };  

  const clearCanvas = () => {
    saveUndoSnapshot();
    setShapes([]);
    setSelectedShapeId(null);
  };  

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSaveToFile();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        if (p5Instance.current) p5Instance.current.saveArt('png');
      } else if (e.key.toLowerCase() === 'r') {
          e.preventDefault();
          generateRandomShape();      
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteShape();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (selectedShapeId) {
          const shapeToCopy = shapes.find(s => s.id === selectedShapeId);
          if (shapeToCopy) {
            copiedShapeRef.current = JSON.parse(JSON.stringify(shapeToCopy));
          }
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (copiedShapeRef.current) {
          const newShape = {
            ...copiedShapeRef.current,
            id: Date.now(), 
            x: copiedShapeRef.current.x + 10,
            y: copiedShapeRef.current.y + 10
          };
          addShapeAndSaveState(newShape);
          setSelectedShapeId(newShape.id);
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleUndo, handleRedo, handleDeleteShape, shapes, selectedShapeId]);

  const saveCustomPalette = () => {
    const newPalette = {
      name: newPaletteName || `Palette ${allPalettes.length + 1}`,
      colors: newPaletteColors,
      custom: true 
    };

    const existingIndex = allPalettes.findIndex(p => p.name === newPalette.name);
    let updated;
    if (existingIndex >= 0) {
      updated = [...allPalettes];
      updated[existingIndex] = newPalette;
    } else {
      updated = [...allPalettes, newPalette];
    }
    setAllPalettes(updated);
    onPaletteChange(newPalette); 
    setNewPaletteName('');
    setNewPaletteColors(['#ffffff', '#ffffff', '#ffffff']);
    setShowCustomModal(false);
  };

  const deletePalette = () => {
    const updated = allPalettes.filter(p => p.name !== newPaletteName);
    setAllPalettes(updated);

    if (selectedPalette.name === newPaletteName) {
      onPaletteChange(updated[0] || {}); 
    }
    setNewPaletteName('');
    setNewPaletteColors(['#ffffff', '#ffffff', '#ffffff']);
    setShowCustomModal(false);
  };

  const updateShapeWithoutUndo = (property, value) => {
    if (selectedShapeId !== null) {
      const updatedShapes = shapes.map(shape =>
        shape.id === selectedShapeId ? { ...shape, [property]: value } : shape
      );
      setShapes(updatedShapes);
    }
  };

return (
  <div className={darkMode ? "dark-mode" : ""}>
    {}
    <div ref={canvasRef} className="canvas-container"></div>

    {}
    <div className="controls-panel">
      <h1 className="app-title">Geometrism</h1>

      {}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setColorPalettesOpen(!colorPalettesOpen)}
        >
          <span>Color Palettes</span>
          <span>{colorPalettesOpen ? "▲" : "▼"}</span>
        </div>
        {colorPalettesOpen && (
          <div className="section-content">
            <div className="palette-selector">
              {allPalettes.map((palette) => (
                <div
                  key={palette.name}
                  className={`palette-option ${
                    palette.name === selectedPalette.name ? "selected" : ""
                  }`}
                  onClick={() => onPaletteChange(palette)}
                  title={palette.name}
                >
                  <label className="palette-name">{palette.name}</label>
                  <div>
                    {palette.colors.map((col, idx) => (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          backgroundColor: col,
                          marginRight: '2px',
                          borderRadius: '3px'
                        }}
                      ></span>
                    ))}
                  </div>
                    <button
                      className="edit-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewPaletteName(palette.name);
                        setNewPaletteColors(palette.colors);
                        setShowCustomModal(true);
                      }}
                    >
                      Edit
                    </button>
                </div>
              ))}
            </div>
            <button
              className="create-button"
              onClick={() => setShowCustomModal(true)}
            >
              Create Custom Palette
            </button>
          </div>
        )}
      </div>

      {}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setRandomSettingsOpen(!randomSettingsOpen)}
        >
          <span>Random Shape Generator Settings</span>
          <span>{randomSettingsOpen ? "▲" : "▼"}</span>
        </div>
        {randomSettingsOpen && (
          <div className="section-content">
            <div className="control-column">
              <div 
                className="allowed-shapes-dropdown-header"
                onClick={() => setAllowedShapesOpen(!allowedShapesOpen)}
              >
                <span className="small-label">Allowed shapes</span>
                <span className="dropdown-arrow">{allowedShapesOpen ? "▲" : "▼"}</span>
              </div>
              {allowedShapesOpen && (
                <div className="allowed-shapes-options">
                  {Object.keys(allowedShapes).map((shapeType) => (
                    <label key={shapeType}>
                      <input
                        type="checkbox"
                        checked={allowedShapes[shapeType]}
                        onChange={(e) =>
                          setAllowedShapes((prev) => ({
                            ...prev,
                            [shapeType]: e.target.checked,
                          }))
                        }
                      />
                      {shapeType.charAt(0).toUpperCase() + shapeType.slice(1)}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="control-column">
              <label className="small-label">Color:</label>
              <div className="color-options-radio">
                <label>
                  <input
                    type="radio"
                    name="randomShapeColorOption"
                    value="random"
                    checked={randomShapeSettings.colorOption === "random"}
                    onChange={() =>
                      setRandomShapeSettings(prev => ({ ...prev, colorOption: "random" }))
                    }
                  />{" "}
                  Random color from selected palette
                </label>
                <label>
                  <input
                    type="radio"
                    name="randomShapeColorOption"
                    value="specific"
                    checked={randomShapeSettings.colorOption === "specific"}
                    onChange={() =>
                      setRandomShapeSettings(prev => ({ ...prev, colorOption: "specific" }))
                    }
                  />{" "}
                  Specific
                </label>
              </div>
              {randomShapeSettings.colorOption === "specific" && (
                <div className="control-row-colors">
                  <div className="color-options">
                    {selectedPalette.colors.map((col, idx) => (
                      <div
                        key={idx}
                        className="color-swatch"
                        style={{
                          backgroundColor: col,
                          border: randomShapeSettings.specificColor === col
                            ? "2px solid #7a6f5a"
                            : "1px solid #ccc",
                        }}
                        onClick={() =>
                          setRandomShapeSettings(prev => ({ ...prev, specificColor: col }))
                        }
                      ></div>
                    ))}
                    <input
                      type="color"
                      className="color-input"
                      value={randomShapeSettings.specificColor}
                      onChange={(e) =>
                        setRandomShapeSettings(prev => ({ ...prev, specificColor: e.target.value }))
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            {}
            <div className="control-row">
              <div className="label-row">
                <label>Size:</label>
                <span>{randomShapeSettings.size.min} - {randomShapeSettings.size.max}</span>
              </div>
              <Range
                values={[randomShapeSettings.size.min, randomShapeSettings.size.max]}
                step={1}
                min={10}
                max={600}
                onChange={(values) => setRandomShapeSettings(prev => ({ ...prev, size: { min: values[0], max: values[1] } }))}
                renderTrack={({ props, children }) => (
                  <div {...props} style={{ ...props.style, height: '8px', background: '#e0e0e0', borderRadius: '4px', flex: 1, margin: '0 5px' }}>
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div {...props} style={{ ...props.style, height: '10px', width: '10px', backgroundColor: '#888', borderRadius: '50%' }} />
                )}
              />
            </div>
            {}
            <div className="control-row">
              <div className="label-row">
                <label style={{ width: '50px' }}>Opacity:</label>
                <span>
                  {randomShapeSettings.opacity.min} - {randomShapeSettings.opacity.max}
                </span>
              </div>
              <Range
                values={[randomShapeSettings.opacity.min, randomShapeSettings.opacity.max]}
                step={1}
                min={0}
                max={255}
                onChange={(values) =>
                  setRandomShapeSettings(prev => ({ 
                    ...prev, 
                    opacity: { min: values[0], max: values[1] } 
                  }))
                }
                renderTrack={({ props, children }) => (
                  <div {...props} style={{ ...props.style, height: '8px', background: '#e0e0e0', borderRadius: '4px', flex: 1, margin: '0 5px' }}>
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div {...props} style={{ ...props.style, height: '10px', width: '10px', backgroundColor: '#888', borderRadius: '50%' }} />
                )}
              />
            </div>
            {}
            <div className="control-row">
              <div className="label-row">
                <label style={{ width: '50px' }}>Rotation:</label>
                <span>
                  {randomShapeSettings.rotation.min} - {randomShapeSettings.rotation.max}
                </span>
              </div>
              <Range
                values={[randomShapeSettings.rotation.min, randomShapeSettings.rotation.max]}
                step={1}
                min={0}
                max={360}
                onChange={(values) =>
                  setRandomShapeSettings(prev => ({ 
                    ...prev, 
                    rotation: { min: values[0], max: values[1] } 
                  }))
                }
                renderTrack={({ props, children }) => (
                  <div {...props} style={{ ...props.style, height: '8px', background: '#e0e0e0', borderRadius: '4px', flex: 1, margin: '0 5px' }}>
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div {...props} style={{ ...props.style, height: '10px', width: '10px', backgroundColor: '#888', borderRadius: '50%' }} />
                )}
              />
            </div>
            {}
            <div className="control-row">
            <div className="label-row">
                <label>Border width:</label>
                <span>
                  {randomShapeSettings.borderWidth.min} - {randomShapeSettings.borderWidth.max}
                </span>
              </div>
              <Range
                values={[randomShapeSettings.borderWidth.min, randomShapeSettings.borderWidth.max]}
                step={1}
                min={0}
                max={20}
                onChange={(values) =>
                  setRandomShapeSettings(prev => ({ 
                    ...prev, 
                    borderWidth: { min: values[0], max: values[1] } 
                  }))
                }
                renderTrack={({ props, children }) => (
                  <div {...props} style={{ ...props.style, height: '8px', background: '#e0e0e0', borderRadius: '4px', flex: 1, margin: '0 5px' }}>
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div {...props} style={{ ...props.style, height: '10px', width: '10px', backgroundColor: '#888', borderRadius: '50%' }} />
                )}
              />
            </div>
            {}
            <div className="control-row">
              <div className="label-row">
                <label>Border opacity:</label>
                <span>
                  {randomShapeSettings.borderOpacity.min} - {randomShapeSettings.borderOpacity.max}
                </span>
              </div>
              <Range
                values={[randomShapeSettings.borderOpacity.min, randomShapeSettings.borderOpacity.max]}
                step={1}
                min={0}
                max={255}
                onChange={(values) =>
                  setRandomShapeSettings(prev => ({ 
                    ...prev, 
                    borderOpacity: { min: values[0], max: values[1] } 
                  }))
                }
                renderTrack={({ props, children }) => (
                  <div {...props} style={{ ...props.style, height: '8px', background: '#e0e0e0', borderRadius: '4px', flex: 1, margin: '0 5px' }}>
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div {...props} style={{ ...props.style, height: '10px', width: '10px', backgroundColor: '#888', borderRadius: '50%' }} />
                )}
              />
            </div>
            {}
            <div className="control-column">
              <label className="small-label">Border color option:</label>
              <div className="color-options-radio">
                <label>
                  <input
                    type="radio"
                    name="randomBorderColorOption"
                    value="random"
                    checked={randomBorderColorOption === "random"}
                    onChange={(e) => setRandomBorderColorOption(e.target.value)}
                  />{" "}
                  Random color from selected palette
                </label>
                <label>
                  <input
                    type="radio"
                    name="randomBorderColorOption"
                    value="same"
                    checked={randomBorderColorOption === "same"}
                    onChange={(e) => setRandomBorderColorOption(e.target.value)}
                  />{" "}
                  Same as Shape
                </label>
                <label>
                  <input
                    type="radio"
                    name="randomBorderColorOption"
                    value="specific"
                    checked={randomBorderColorOption === "specific"}
                    onChange={(e) => setRandomBorderColorOption(e.target.value)}
                  />{" "}
                  Specific
                </label>
              </div>
              {randomBorderColorOption === "specific" && (
                <div className="color-options">
                  {selectedPalette.colors.map((col, idx) => (
                    <div
                      key={idx}
                      className="color-swatch"
                      style={{
                        backgroundColor: col,
                        border: randomBorderColor === col ? "2px solid #7a6f5a" : "1px solid #ccc",
                      }}
                      onClick={() => setRandomBorderColor(col)}
                    ></div>
                  ))}
                  <input
                    type="color"
                    className="color-input"
                    value={randomBorderColor}
                    onChange={(e) => setRandomBorderColor(e.target.value)}
                  />
                </div>
              )}
              {}
              <button className="create-button" onClick={generateRandomShape}>
                Generate Random Shape
              </button>
            </div>
          </div>
        )}
      </div>
      {}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setManualAddSettingsOpen(!manualAddSettingsOpen)}
        >
          <span>Click-to-Add Shape Options</span>
          <span>{manualAddSettingsOpen ? "▲" : "▼"}</span>
        </div>
        {manualAddSettingsOpen && (
          <div className="section-content">

            <div className="label-row">
                <label>Shape type:</label>
                <select
                  value={selectedShapeType}
                  onChange={(e) => setSelectedShapeType(e.target.value)}
                >
                  <option value="circle">Circle</option>
                  <option value="square">Square</option>
                  <option value="rectangle">Rectangle</option>
                  <option value="line">Line</option>
                  <option value="triangle">Triangle</option>
                  <option value="pentagon">Pentagon</option>
                  <option value="hexagon">Hexagon</option>
                  <option value="partialCircle">Partial Circle</option>
                </select>
              </div>
              {(selectedShapeType === "rectangle" || selectedShapeType === "line") ? (
                <>
                  <div className="control-row-input">
                    <div className="label-row-input">
                      <label>Width:</label>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        value={defaultShapeSettings.width}
                        onChange={(e) =>
                          setDefaultShapeSettings((prev) => ({
                            ...prev,
                            width: parseInt(e.target.value, 10),
                          }))
                        }
                      />
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="2000"
                      value={defaultShapeSettings.width}
                      onChange={(e) =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          width: parseInt(e.target.value, 10),
                        }))
                      }
                    />
                  </div>
                  <div className="control-row-input">
                    <div className="label-row-input">
                      <label>Height:</label>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        value={defaultShapeSettings.height}
                        onChange={(e) =>
                          setDefaultShapeSettings((prev) => ({
                            ...prev,
                            height: parseInt(e.target.value, 10),
                          }))
                        }
                      />
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="2000"
                      value={defaultShapeSettings.height}
                      onChange={(e) =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          height: parseInt(e.target.value, 10),
                        }))
                      }
                    />
                  </div>
                </>
              ) : (
                <div className="control-row-input">
                  <div className="label-row-input">
                    <label>Size:</label>
                    <input
                      type="number"
                      min="10"
                      max="200"
                      value={defaultShapeSettings.size}
                      onChange={(e) =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          size: parseInt(e.target.value, 10),
                        }))
                      }
                    />
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    value={defaultShapeSettings.size}
                    onChange={(e) =>
                      setDefaultShapeSettings((prev) => ({
                        ...prev,
                        size: parseInt(e.target.value, 10),
                      }))
                    }
                  />
                </div>
              )}
              <div className="control-column">
                <label className="small-label">Color option:</label>
                <div className="color-options-radio">
                  <label>
                    <input
                      type="radio"
                      name="manualColorOption"
                      value="random"
                      checked={defaultShapeSettings.useRandomColor}
                      onChange={() =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          useRandomColor: true,
                        }))
                      }
                    />{" "}
                    Random color from selected palette
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="manualColorOption"
                      value="specific"
                      checked={!defaultShapeSettings.useRandomColor}
                      onChange={() =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          useRandomColor: false,
                        }))
                      }
                    />{" "}
                    Specific
                  </label>
                </div>
              </div>
              {!defaultShapeSettings.useRandomColor && (
                <div className="control-row-colors">
                  {}
                  <div className="color-options">
                    {selectedPalette.colors.map((col, idx) => (
                      <div
                        key={idx}
                        className="color-swatch"
                        style={{
                          backgroundColor: col,
                          border:
                            defaultShapeSettings.color === col
                              ? "2px solid #7a6f5a"
                              : "1px solid #ccc",
                        }}
                        onClick={() =>
                          setDefaultShapeSettings((prev) => ({
                            ...prev,
                            color: col,
                          }))
                        }
                      ></div>
                    ))}
                    <input
                      type="color"
                      className="color-input"
                      value={defaultShapeSettings.color}
                      onChange={(e) =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          color: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Opacity:</label>
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={defaultShapeSettings.opacity}
                    onChange={(e) =>
                      setDefaultShapeSettings((prev) => ({
                        ...prev,
                        opacity: parseInt(e.target.value, 10),
                      }))
                    }
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={defaultShapeSettings.opacity}
                  onChange={(e) =>
                    setDefaultShapeSettings((prev) => ({
                      ...prev,
                      opacity: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                <label>Border width:</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={defaultShapeSettings.borderWidth}
                  onChange={(e) =>
                    setDefaultShapeSettings((prev) => ({
                      ...prev,
                      borderWidth: parseInt(e.target.value, 10),
                    }))
                  }
                />
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={defaultShapeSettings.borderWidth}
                  onChange={(e) =>
                    setDefaultShapeSettings((prev) => ({
                      ...prev,
                      borderWidth: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              {}
              <div className="control-row-colors">
                <label>Border color:</label>
                <div className="color-options">
                  {selectedPalette.colors.map((col, idx) => (
                    <div
                      key={idx}
                      className="color-swatch"
                      style={{
                        backgroundColor: col,
                        border:
                          defaultShapeSettings.borderColor === col
                            ? "2px solid #7a6f5a"
                            : "1px solid #ccc",
                      }}
                      onClick={() =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          borderColor: col,
                        }))
                      }
                    ></div>
                  ))}
                  <input
                    type="color"
                    className="color-input"
                    value={defaultShapeSettings.borderColor}
                    onChange={(e) =>
                      setDefaultShapeSettings((prev) => ({
                        ...prev,
                        borderColor: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Border opacity:</label>
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={defaultShapeSettings.borderOpacity}
                    onChange={(e) =>
                      setDefaultShapeSettings((prev) => ({
                        ...prev,
                        borderOpacity: parseInt(e.target.value, 10),
                      }))
                    }
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={defaultShapeSettings.borderOpacity}
                  onChange={(e) =>
                    setDefaultShapeSettings((prev) => ({
                      ...prev,
                      borderOpacity: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Rotation:</label>
                  <input
                    type="number"
                    min="0"
                    max="360"
                    value={defaultShapeSettings.rotation}
                    onChange={(e) =>
                      setDefaultShapeSettings((prev) => ({
                        ...prev,
                        rotation: parseInt(e.target.value, 10),
                      }))
                    }
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={defaultShapeSettings.rotation}
                  onChange={(e) =>
                    setDefaultShapeSettings((prev) => ({
                      ...prev,
                      rotation: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              {selectedShapeType === "partialCircle" && (
                <>
                  <div className="control-row-input">
                    <div className="label-row-input">
                      <label>Arc Start:</label>
                      <input
                        type="number"
                        min="0"
                        max="360"
                        value={defaultShapeSettings.arcStart}
                        onChange={(e) =>
                          setDefaultShapeSettings((prev) => ({
                            ...prev,
                            arcStart: parseInt(e.target.value, 10),
                          }))
                        }
                      />
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={defaultShapeSettings.arcStart}
                      onChange={(e) =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          arcStart: parseInt(e.target.value, 10),
                        }))
                      }
                    />
                  </div>
                  <div className="control-row-input">
                    <div className="label-row-input">
                      <label>Arc End:</label>
                      <input
                        type="number"
                        min="0"
                        max="360"
                        value={defaultShapeSettings.arcEnd}
                        onChange={(e) =>
                          setDefaultShapeSettings((prev) => ({
                            ...prev,
                            arcEnd: parseInt(e.target.value, 10),
                          }))
                        }
                      />
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={defaultShapeSettings.arcEnd}
                      onChange={(e) =>
                        setDefaultShapeSettings((prev) => ({
                          ...prev,
                          arcEnd: parseInt(e.target.value, 10),
                        }))
                      }
                    />
                  </div>
                </>
              )}

          </div>
        )}
      </div>

      {}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setGridSettingsOpen(!gridSettingsOpen)}
        >
          <span>Grid Options</span>
          <span>{gridSettingsOpen ? "▲" : "▼"}</span>
        </div>
        {gridSettingsOpen && (
          <div className="section-content">
            <div className="control-row-checkbox">
              <label>Show grid:</label>
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
              />
            </div>
            <div className="control-row-input">
              <div className="label-row-input">
                <label>Grid (px):</label>
                <input
                  type="number"
                  min="10"
                  max="200"
                  value={gridSize}
                  onChange={(e) =>
                    setGridSize(parseInt(e.target.value, 10))
                  }
                />
              </div>
              <input
                type="range"
                min="10"
                max="200"
                value={gridSize}
                onChange={(e) =>
                  setGridSize(parseInt(e.target.value, 10))
                }
              />
            </div>
            <div className="control-row-checkbox">
              <label>Snap to the grid:</label>
              <input
                type="checkbox"
                checked={snapToGrid}
                onChange={(e) => setSnapToGrid(e.target.checked)}
              />
            </div>
          </div>
        )}
      </div>

      {}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setFreehandSettingsOpen(!freehandSettingsOpen)}
        >
          <span>Freehand Drawing Options</span>
          <span>{freehandSettingsOpen ? "▲" : "▼"}</span>
        </div>
        {freehandSettingsOpen && (
          <div className="section-content">
            <div className="control-row-checkbox">
              <label>Freehand mode:</label>
              <input
                type="checkbox"
                checked={freehandMode}
                onChange={(e) => setFreehandMode(e.target.checked)}
              />
            </div>
            <div className="control-row-input">
              <div className="label-row-input">
                <label>Line width:</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={freehandSettings.lineWidth}
                  onChange={(e) =>
                    setFreehandSettings((prev) => ({
                      ...prev,
                      lineWidth: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              <input
                type="range"
                min="1"
                max="60"
                value={freehandSettings.lineWidth}
                onChange={(e) =>
                  setFreehandSettings((prev) => ({
                    ...prev,
                    lineWidth: parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div className="control-row-colors">
              <label>Color:</label>
              <div className="color-options">
                {selectedPalette.colors.map((col, idx) => (
                  <div
                    key={idx}
                    className="color-swatch"
                    style={{
                      backgroundColor: col,
                      border:
                        freehandSettings.color === col
                        ? "2px solid #7a6f5a"
                        : "1px solid #ccc",
                    }}
                    onClick={() =>
                      setFreehandSettings((prev) => ({...prev, color: col}))
                    }
                  ></div>
                ))}
                <input
                  type="color"
                  className="color-input"
                  value={freehandSettings.color}
                  onChange={(e) =>
                    setFreehandSettings((prev) => ({ ...prev, color: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setGlobalControlsOpen(!globalControlsOpen)}
        >
          <span>Canvas Controls</span>
          <span>{globalControlsOpen ? "▲" : "▼"}</span>
        </div>
        {globalControlsOpen && (
          <div className="section-content">
            <div className="control-row-colors">
              <label>Background color:</label>
              <div className="color-options">
                {selectedPalette.colors.map((col, idx) => (
                  <div
                    key={idx}
                    className="color-swatch"
                    style={{
                      backgroundColor: col,
                      border: bgColor === col ? "2px solid #7a6f5a" : "1px solid #ccc",
                    }}
                    onClick={() => {
                      setBgColor(col);
                      setBgColorOverridden(true);
                    }}
                  ></div>
                ))}
                <input
                  type="color"
                  className="color-input"
                  value={bgColor}
                  onChange={(e) => {
                    setBgColor(e.target.value);
                    setBgColorOverridden(true);
                  }}
                />
              </div>
            </div>
            <div className="buttons-row">
              <button onClick={handleUndo}>Undo</button>
              <button onClick={handleRedo}>Redo</button>
              <button onClick={clearCanvas}>Clear Canvas</button>
            </div>
            <div className="buttons-row">
              <button
                onClick={() =>
                  p5Instance.current && p5Instance.current.saveArt("png")
                }
              >
                Export PNG
              </button>
              <button
                onClick={() =>
                  p5Instance.current && p5Instance.current.saveArt("svg")
                }
              >
                Export SVG
              </button>
              <button onClick={handleSaveToFile}>Export JSON</button>
            </div>
            <input type="file" accept=".json" onChange={handleLoad} id="jsonLoader" style={{ display: "none" }} />
            <button onClick={() => document.getElementById('jsonLoader').click()}>Load JSON</button>
          </div>
        )}
      </div>

      {}
      {selectedShapeId && (
        <div className="edit-controls-locked">
          <div
            className="section-header"
            onClick={() => setEditShapeSettingsOpen(!editShapeSettingsOpen)}
          >
            <span>Edit Selected Shape</span>
            <span>{editShapeSettingsOpen ? "▲" : "▼"}</span>
          </div>
          {editShapeSettingsOpen && (
            <div className="section-content">
              {!(shapes.find(s => s.id === selectedShapeId)?.type === "rectangle" ||
                shapes.find(s => s.id === selectedShapeId)?.type === "line") && (
                <div className="control-row-input">
                  <div className="label-row-input">
                    <label>Size:</label>
                    <input
                      type="number"
                      min="10"
                      max="200"
                      value={shapes.find(s => s.id === selectedShapeId)?.size || 0}
                      onMouseDown={() => {
                        if (selectedShapeId && !sizeSliderCommitFlag.current) {
                          saveUndoSnapshot();
                          sizeSliderCommitFlag.current = true;
                        }
                      }}
                      onChange={(e) =>
                        updateShapeWithoutUndo("size", parseInt(e.target.value, 10))
                      }
                      onBlur={() => {
                        sizeSliderCommitFlag.current = false;
                      }}
                    />
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    value={shapes.find(s => s.id === selectedShapeId)?.size || 0}
                    onMouseDown={() => {
                      if (selectedShapeId && !sizeSliderCommitFlag.current) {
                        saveUndoSnapshot();
                        sizeSliderCommitFlag.current = true;
                      }
                    }}
                    onChange={(e) =>
                      updateShapeWithoutUndo("size", parseInt(e.target.value, 10))
                    }
                    onMouseUp={() => {
                      sizeSliderCommitFlag.current = false;
                    }}
                  />
                </div>
              )}
              {(shapes.find(s => s.id === selectedShapeId)?.type === "rectangle" ||
                shapes.find(s => s.id === selectedShapeId)?.type === "line") && (
                <>
                  <div className="control-row-input">
                    <div className="label-row-input">
                      <label>Width:</label>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        value={shapes.find(s => s.id === selectedShapeId)?.width || 0}
                        onChange={(e) =>
                          updateShapeWithoutUndo("width", parseInt(e.target.value, 10))
                        }
                      />
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="2000"
                      value={shapes.find(s => s.id === selectedShapeId)?.width || 0}
                      onChange={(e) =>
                        updateShapeWithoutUndo("width", parseInt(e.target.value, 10))
                      }
                    />
                  </div>
                  <div className="control-row-input">
                    <div className="label-row-input">
                      <label>Height:</label>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        value={shapes.find(s => s.id === selectedShapeId)?.height || 0}
                        onChange={(e) =>
                          updateShapeWithoutUndo("height", parseInt(e.target.value, 10))
                        }
                      />
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="2000"
                      value={shapes.find(s => s.id === selectedShapeId)?.height || 0}
                      onChange={(e) =>
                        updateShapeWithoutUndo("height", parseInt(e.target.value, 10))
                      }
                    />
                  </div>
                </>
              )}
              <div className="control-row-colors">
                <label>Color:</label>
                <div className="color-options">
                  {selectedPalette.colors.map((col, idx) => (
                    <div
                      key={idx}
                      className="color-swatch"
                      style={{
                        backgroundColor: col,
                        border: shapes.find(s => s.id === selectedShapeId)?.color === col
                          ? "2px solid #7a6f5a"
                          : "1px solid #ccc",
                      }}
                      onClick={() => handleShapeEdit("color", col)}
                    ></div>
                  ))}
                  <input
                    type="color"
                    className="color-input"
                    onChange={(e) => handleShapeEdit("color", e.target.value)}
                  />
                </div>
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Opacity:</label>
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={shapes.find(s => s.id === selectedShapeId)?.opacity || 255}
                    onMouseDown={() => {
                      if (selectedShapeId && !opacitySliderCommitFlag.current) {
                        saveUndoSnapshot();
                        opacitySliderCommitFlag.current = true;
                      }
                    }}
                    onChange={(e) =>
                      updateShapeWithoutUndo("opacity", parseInt(e.target.value, 10))
                    }
                    onBlur={() => {
                      opacitySliderCommitFlag.current = false;
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={shapes.find(s => s.id === selectedShapeId)?.opacity || 255}
                  onMouseDown={() => {
                    if (selectedShapeId && !opacitySliderCommitFlag.current) {
                      saveUndoSnapshot();
                      opacitySliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("opacity", parseInt(e.target.value, 10))
                  }
                  onMouseUp={() => {
                    opacitySliderCommitFlag.current = false;
                  }}
                />
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Border width:</label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={
                      selectedShapeId
                        ? shapes.find((s) => s.id === selectedShapeId)?.borderWidth ?? 0
                        : 0
                    }
                    onMouseDown={() => {
                      if (selectedShapeId && !borderWidthSliderCommitFlag.current) {
                        saveUndoSnapshot();
                        borderWidthSliderCommitFlag.current = true;
                      }
                    }}
                    onChange={(e) =>
                      updateShapeWithoutUndo("borderWidth", parseInt(e.target.value, 10))
                    }
                    onBlur={() => {
                      borderWidthSliderCommitFlag.current = false;
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={
                    selectedShapeId
                      ? shapes.find((s) => s.id === selectedShapeId)?.borderWidth ?? 0
                      : 0
                  }
                  onMouseDown={() => {
                    if (selectedShapeId && !borderWidthSliderCommitFlag.current) {
                      saveUndoSnapshot();
                      borderWidthSliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("borderWidth", parseInt(e.target.value, 10))
                  }
                  onMouseUp={() => {
                    borderWidthSliderCommitFlag.current = false;
                  }}
                />
              </div>
              <div className="control-row-colors">
                <label>Border color:</label>
                <div className="color-options">
                  {selectedPalette.colors.map((col, idx) => (
                    <div
                      key={idx}
                      className="color-swatch"
                      style={{
                        backgroundColor: col,
                        border: shapes.find(s => s.id === selectedShapeId)?.borderColor === col
                          ? "2px solid #7a6f5a"
                          : "1px solid #ccc",
                      }}
                      onClick={() => handleShapeEdit("borderColor", col)}
                    ></div>
                  ))}
                  <input
                    type="color"
                    className="color-input"
                    onChange={(e) => handleShapeEdit("borderColor", e.target.value)}
                  />
                </div>
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Border opacity:</label>
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={shapes.find(s => s.id === selectedShapeId)?.borderOpacity || 255}
                    onMouseDown={() => {
                      if (selectedShapeId && !borderOpacitySliderCommitFlag.current) {
                        saveUndoSnapshot();
                        borderOpacitySliderCommitFlag.current = true;
                      }
                    }}
                    onChange={(e) =>
                      updateShapeWithoutUndo("borderOpacity", parseInt(e.target.value, 10))
                    }
                    onBlur={() => {
                      borderOpacitySliderCommitFlag.current = false;
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={shapes.find(s => s.id === selectedShapeId)?.borderOpacity || 255}
                  onMouseDown={() => {
                    if (selectedShapeId && !borderOpacitySliderCommitFlag.current) {
                      saveUndoSnapshot();
                      borderOpacitySliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("borderOpacity", parseInt(e.target.value, 10))
                  }
                  onMouseUp={() => {
                    borderOpacitySliderCommitFlag.current = false;
                  }}
                />
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>X position:</label>
                  <input
                    type="number"
                    min="0"
                    max={window.innerWidth - 300}
                    value={
                      selectedShapeId
                        ? shapes.find((s) => s.id === selectedShapeId)?.x ?? 0
                        : 0
                    }
                    onMouseDown={() => {
                      if (selectedShapeId && !xSliderCommitFlag.current) {
                        saveUndoSnapshot();
                        xSliderCommitFlag.current = true;
                      }
                    }}
                    onChange={(e) =>
                      updateShapeWithoutUndo("x", parseFloat(e.target.value))
                    }
                    onBlur={() => {
                      xSliderCommitFlag.current = false;
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max={window.innerWidth - 300}
                  value={
                    selectedShapeId
                      ? shapes.find((s) => s.id === selectedShapeId)?.x ?? 0
                      : 0
                  }
                  onMouseDown={() => {
                    if (selectedShapeId && !xSliderCommitFlag.current) {
                      saveUndoSnapshot();
                      xSliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("x", parseFloat(e.target.value))
                  }
                  onMouseUp={() => {
                    xSliderCommitFlag.current = false;
                  }}
                />
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                <label>Y position:</label>
                <input
                  type="number"
                  min="0"
                  max={window.innerHeight}
                  value={
                    selectedShapeId
                      ? shapes.find((s) => s.id === selectedShapeId)?.y ?? 0
                      : 0
                  }
                  onMouseDown={() => {
                    if (selectedShapeId && !ySliderCommitFlag.current) {
                      saveUndoSnapshot();
                      ySliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("y", parseFloat(e.target.value))
                  }
                  onBlur={() => {
                    ySliderCommitFlag.current = false;
                  }}
                />
                </div>
                <input
                  type="range"
                  min="0"
                  max={window.innerHeight}
                  value={
                    selectedShapeId
                      ? shapes.find((s) => s.id === selectedShapeId)?.y ?? 0
                      : 0
                  }
                  onMouseDown={() => {
                    if (selectedShapeId && !ySliderCommitFlag.current) {
                      saveUndoSnapshot();
                      ySliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("y", parseFloat(e.target.value))
                  }
                  onMouseUp={() => {
                    ySliderCommitFlag.current = false;
                  }}
                />
              </div>
              <div className="control-row-input">
                <div className="label-row-input">
                  <label>Rotation:</label>
                  <input
                    type="number"
                    min="0"
                    max="360"
                    value={shapes.find((s) => s.id === selectedShapeId)?.rotation || 0}
                    onMouseDown={() => {
                      if (selectedShapeId && !rotationSliderCommitFlag.current) {
                        saveUndoSnapshot();
                        rotationSliderCommitFlag.current = true;
                      }
                    }}
                    onChange={(e) =>
                      updateShapeWithoutUndo("rotation", parseFloat(e.target.value))
                    }
                    onBlur={() => {
                      rotationSliderCommitFlag.current = false;
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={shapes.find((s) => s.id === selectedShapeId)?.rotation || 0}
                  onMouseDown={() => {
                    if (selectedShapeId && !rotationSliderCommitFlag.current) {
                      saveUndoSnapshot();
                      rotationSliderCommitFlag.current = true;
                    }
                  }}
                  onChange={(e) =>
                    updateShapeWithoutUndo("rotation", parseFloat(e.target.value))
                  }
                  onMouseUp={() => {
                    rotationSliderCommitFlag.current = false;
                  }}
                />
              </div>
              {shapes.find((s) => s.id === selectedShapeId)?.type === "partialCircle" && (
                <div>
                  <div className="control-row-input">
                  <div className="label-row-input">
                    <label>Arc Start:</label>
                    <input
                      type="number"
                      min="0"
                      max="360"
                      value={shapes.find((s) => s.id === selectedShapeId)?.arcStart || 0}
                      onChange={(e) =>
                        handleShapeEdit("arcStart", parseInt(e.target.value, 10))
                      }
                    />
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={shapes.find((s) => s.id === selectedShapeId)?.arcStart || 0}
                    onChange={(e) =>
                      handleShapeEdit("arcStart", parseInt(e.target.value, 10))
                    }
                  />
                </div>
                <div className="control-row-input">
                  <div className="label-row-input">
                    <label>Arc End:</label>
                    <input
                      type="number"
                      min="0"
                      max="360"
                      value={shapes.find((s) => s.id === selectedShapeId)?.arcEnd || 90}
                      onChange={(e) =>
                        handleShapeEdit("arcEnd", parseInt(e.target.value, 10))
                      }
                    />
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={shapes.find((s) => s.id === selectedShapeId)?.arcEnd || 90}
                    onChange={(e) =>
                      handleShapeEdit("arcEnd", parseInt(e.target.value, 10))
                    }
                  />
                </div>
              </div>
              )}
              <div className="buttons-row">
                <button onClick={handleDeleteShape}>Delete</button>
                <button onClick={() => moveShape("forward")}>Move Forward</button>
                <button onClick={() => moveShape("backward")}>Move Backward</button>
              </div>
            </div>
          )}
        </div>
      )}

      {}
      {}
      {}
      <div className="bottom-controls">
      <button className="dark-mode-toggle" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? "Light Mode" : "Dark Mode"}
        </button>
        <button className="help-button" onClick={() => setShowHelp(true)}>
          ?
        </button>
      </div>

      {}
      {showCustomModal && (
        <PaletteModal
          onClose={() => setShowCustomModal(false)}
          newPaletteName={newPaletteName}
          setNewPaletteName={setNewPaletteName}
          newPaletteColors={newPaletteColors}
          setNewPaletteColors={setNewPaletteColors}
          saveCustomPalette={saveCustomPalette}
          onDelete={deletePalette}
        />
      )}

      {}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

    </div>
  </div>
);

});

export default Canvas;